import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first } from 'lodash-es';
import { DealFilterService } from 'projects/semeris/src/features/deal-filter/services/deal-filter.service';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { Observable } from 'rxjs';
import { filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { DealFilterActions } from './deal-filter.actions';
import { DealFilterSelectors } from './deal-filter.selectors';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import {
  WebSocketEntities,
  WebSocketReloadEvent,
} from '../../features/shared/models/web-socket/web-socket.models';
import {
  createWebSocketEffect,
  createWebSocketWithWarningEffect,
} from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class DealFilterEffects {
  private service = inject(DealFilterService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly webSocketService = inject(WebSocketService);

  private readonly notifications: PushNotificationRecord = {
    [DealFilterActions.CreateOrUpdateDealFilterSuccess.type]: {
      message: 'Deal filter saved',
      isSuccess: true,
    },
    [DealFilterActions.CreateOrUpdateDealFilterFail.type]: {
      message: 'Failed to save the filter',
      isSuccess: false,
    },
  };

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.LoadList),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getAll().pipe(
          map((list) => DealFilterActions.LoadListSuccess({ list, withApply: action.withApply })),
          reportEffectError(DealFilterActions.LoadListFail)
        )
      )
    )
  );

  loadListApplyEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.LoadListSuccess),
      filter((action) => action.withApply),
      withLatestFrom(this.store),
      map(([action, store]) =>
        store.dealFilter.selectedFilterId
          ? DealFilterActions.UseFilter({ id: store.dealFilter.selectedFilterId })
          : DealFilterActions.UseDefaultFilter()
      )
    )
  );

  loadListSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealFilterActions.CreateOrUpdateDealFilterSuccess),
        map((action) =>
          action.returnUrl
            ? this.router.navigateByUrl(action.returnUrl)
            : this.router.navigate([
                '/',
                SemerisRoutes.dealFilter.root,
                SemerisRoutes.dealFilter.filter,
                action.id,
              ])
        )
      ),
    { dispatch: false }
  );

  useDefaultFilterEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseDefaultFilter),
      withLatestFrom(
        this.store.select(DealFilterSelectors.selectedFilter),
        this.store.select(DealFilterSelectors.list)
      ),
      map(([action, dealFilter, list]) =>
        dealFilter.id && list.some((filter) => filter.id === dealFilter.id)
          ? dealFilter.id
          : first(list)?.id
      ),
      filter((id) => !!id),
      map((id) =>
        DealFilterActions.UseFilter({
          id,
          skipSave: true,
        })
      )
    )
  );

  setFilterEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.CreateOrUpdateDealFilterSuccess),
      filter((action) => !!action.returnUrl),
      map((action) => DealFilterActions.UseFilter({ id: action.id }))
    )
  );

  useFilterSaveEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilter),
      filter((action) => !action.skipSave),
      map((action) =>
        UserProfileActions.UpdateAssetSetting({ setting: { selectedDealFilterId: action.id } })
      )
    )
  );

  useFilterQueryEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilter),
      filter((action) => !!action.id),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getById(action.id).pipe(
          map((dealFilter) => DealFilterActions.UseFilterSuccess({ dealFilter })),
          reportEffectError(DealFilterActions.UseFilterFail)
        )
      )
    )
  );

  createDealFilter$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.CreateOrUpdateDealFilter),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const dealFilter = {
          ...action.dealFilter,
          displayName:
            action.dealFilter?.displayName ||
            this.service.generateDisplayName(
              action.dealFilter,
              store.manager.list,
              store.transaction.list
            ),
          sort: store.dealFilter.sort,
        };

        return (
          dealFilter.id
            ? this.service.update(dealFilter, true, false)
            : this.service.create(dealFilter)
        ).pipe(
          map((res) => {
            return DealFilterActions.CreateOrUpdateDealFilterSuccess({
              dealFilters: [...(store.dealFilter.list || []).filter((df) => df.id !== res.id), res],
              id: res.id,
              returnUrl: action.returnUrl,
            });
          }),
          reportEffectError(DealFilterActions.CreateOrUpdateDealFilterFail)
        );
      })
    )
  );

  shareDealFilter$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ShareDealFilter),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(
            {
              id: action.dealFilter.id,
              shared: action.data.accessControl?.shared,
              sharedForEdit: action.data.accessControl?.sharedForEdit,
            } as any,
            false,
            true
          )
          .pipe(
            map((res) =>
              DealFilterActions.ShareDealFilterSuccess({
                dealFilter: {
                  ...action.dealFilter,
                  shared: action.data.accessControl?.shared,
                  sharedForEdit: action.data.accessControl?.sharedForEdit,
                },
              })
            ),
            reportEffectError(DealFilterActions.ShareDealFilterFail)
          )
      )
    )
  );

  shareTopicSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ShareDealFilterSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: action.dealFilter.shared ? 'Deal filter shared' : 'Deal filter share revoked',
          config: {
            duration: 2_000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          },
        })
      )
    )
  );

  deleteFilterEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.DeleteFilter),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.delete(action.id).pipe(
          map(() =>
            DealFilterActions.DeleteFilterSuccess({
              dealFilters: [...(store.dealFilter.list || []).filter((df) => df.id !== action.id)],
              id: action.id,
            })
          ),
          reportEffectError(DealFilterActions.DeleteFilterFail)
        )
      )
    )
  );

  deleteFilterSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealFilterActions.DeleteFilterSuccess),
        map(() => {
          this.router.navigate([
            '/',
            SemerisRoutes.dealFilter.root,
            SemerisRoutes.dealFilter.filter,
            SemerisRoutes.dealFilter.new,
          ]);
        })
      ),
    { dispatch: false }
  );

  applyFilterEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ApplyFilter),
      mergeMap((action) =>
        action.preview
          ? this.service.preview(action.dealFilter).pipe(
              map((dealFilter) => {
                return DealFilterActions.ApplyFilterSuccess({
                  dealFilter,
                  resetSelectedFilter: action.resetSelectedFilter,
                });
              }),
              reportEffectError(DealFilterActions.ApplyFilterFail)
            )
          : this.service.getById(action.dealFilter.id).pipe(
              map((dealFilter) => {
                return DealFilterActions.ApplyFilterSuccess({
                  dealFilter,
                  resetSelectedFilter: action.resetSelectedFilter,
                });
              }),
              reportEffectError(DealFilterActions.ApplyFilterFail)
            )
      )
    )
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  wsEffect$: Observable<Action> = createWebSocketWithWarningEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.DealFilter,
    (state) => DealFilterActions.LoadList({ withApply: true }),
    WebSocketReloadEvent.DealFilterReload
  );

  wsListEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.DealFilter,
    () => DealFilterActions.LoadList({})
  );

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilterFail),
      map(() => AppActions.Unauthorized())
    )
  );
}
